// user-dashboard/hooks/usePortsByEmail.js
import { useMemo } from "react";

/**
 * If a port has no user_email, copy the only email used by that user's other
 * ports, or the only advice email. Multiple emails → leave blank.
 */
export function inferMissingPortEmails(ports, advice = []) {
  const list = ports || [];
  const emailsByUserCode = new Map();

  list.forEach((p) => {
    const uc = (p.user_code || "").trim().toLowerCase();
    const em = (p.user_email || "").trim();
    if (!uc || !em) return;
    if (!emailsByUserCode.has(uc)) emailsByUserCode.set(uc, new Set());
    emailsByUserCode.get(uc).add(em);
  });

  const adviceEmails = [
    ...new Set(
      (advice || []).map((a) => (a.email || "").trim()).filter(Boolean)
    ),
  ];
  const uniqueAdviceEmail = adviceEmails.length === 1 ? adviceEmails[0] : "";

  return list.map((p) => {
    if ((p.user_email || "").trim()) return p;
    const uc = (p.user_code || "").trim().toLowerCase();
    const siblingEmails = emailsByUserCode.get(uc);
    if (siblingEmails && siblingEmails.size === 1) {
      return { ...p, user_email: [...siblingEmails][0] };
    }
    if (uniqueAdviceEmail) {
      return { ...p, user_email: uniqueAdviceEmail };
    }
    return p;
  });
}

/**
 * Groups the user's ports by user_email (case-insensitive).
 *
 * @param {Array} ports - list of port objects
 * @returns {Map<string, Array>} map of email -> ports[]
 */
export default function usePortsByEmail(ports) {
  return useMemo(() => {
    const map = new Map();

    (ports || []).forEach((p) => {
      if (p.archived) return;
      const key = (p.user_email || "").trim().toLowerCase();
      if (!key) return;

      if (!map.has(key)) map.set(key, []);
      map.get(key).push(p);
    });

    // Sort ports inside each email group by port_number (numeric-aware)
    for (const arr of map.values()) {
      arr.sort((a, b) => {
        const av = (a.port_number || "").toString();
        const bv = (b.port_number || "").toString();
        return av.localeCompare(bv, undefined, { numeric: true });
      });
    }

    return map;
  }, [ports]);
}

